import React, { useState, useEffect, useRef, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  IStringHash,
} from "@models";
import { 
  Character,
  CharacterItem,
  CharacterContainer,
  // CharacterFeatureSource,
  CharacterAbility,
  Ability,
  CharacterAction,
  CharacterSpell,
  Attack,
  CharacterInventory,
  CampaignInventory,
  Campaign,
  useCampaignReceivableAlerts,
  Receivable,
} from "@dndModels";

import API from "@utilities/smart_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

import StringBox from "@components/input/strings/StringBox";
import FloatMenu from "@components/input/menus/FloatMenu";

import CharacterCurrency from "@dndComponents/character/inventory/CharacterCurrency";
import CharacterStartingEquipment from "@dndComponents/character/inventory/startingEquipment/CharacterStartingEquipment";

import CharacterViewInventoryItem from "@dndComponents/character/inventory/item/CharacterViewInventoryItem";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import CharacterAttunement from "@dndComponents/character/inventory/CharacterAttunement";
import CharacterActionInput from "@dndComponents/character/spells_and_abilities/CharacterActionInput";
import CharacterItemDetailsSidebar from "@dndComponents/character/inventory/item/CharacterItemDetailsSidebar";
import CharacterManageInventoryButton from "@dndComponents/character/inventory/manage/CharacterManageInventoryButton";
import CharacterSpellDetails from "@dndComponents/character/spells_and_abilities/CharacterSpellDetails";
import CharacterAttackSidebar from "@dndComponents/character/inventory/CharacterAttackSidebar";
import FlexBox from '@components/layout/FlexBox';
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';
import CharacterContainerItem from './item/CharacterContainerItem';

interface Props {
  char: Character | Campaign;
  inventory: CharacterInventory | CampaignInventory;
  disabled: boolean;
  shared: boolean;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

export default function CharacterViewInventory({
  char,
  inventory,
  disabled,
  shared,
}: Props) {
  const [reloading, setReloading] = useState(false);
  const [search_string, setSearchString] = useState("");
  const [view, setView] = useState("ALL");
  const [edit_view, setEditView] = useState(false);
  const [drawer, setDrawer] = useState("");
  const [details_item, setDetailsItem] = useState<CharacterItem | null>(null);
  const [details_action, setDetailsAction] = useState<CharacterAction | null>(null);
  const [reload_time, setReloadTime] = useState(0);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const itemsChanged = useCallback(() => { 
    console.log("Char View Inventory - Items Changed");
    setTimeout(() => {
      setReloading(true);
    }, 100);
  }, []);

  useEffect(() => {
    console.log("Char View Inventory");
    char.subscription_holder.subscribe("Items", mounted, itemsChanged);
  }, [char, itemsChanged]);

  useEffect(() => {
    if (reloading) {
      setReloadTime(common_util.get_time_number());
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderCurrency = () => {
    return (
      <div
        style={{
          float: "right",
          fontSize: "13px",
          fontWeight: "bold",
          cursor: "pointer" 
        }} 
        onClick={() => {
          setDrawer("currency");
        }}>
        Currency: { inventory.money.toString() }
      </div>
    )
  }

  const renderInventory = () => {
    // Breaking this up into containers and equipment
    const filtered = api.filter("", inventory.items, { search_string });
    return (
      <>
        <Grid
          style={{
            fontSize: "14px",
          }}
          className="header"
          size={{
            xs: 12
          }}>
          &nbsp;Inventory
        </Grid>
        { inventory.containers.filter(o => o.true_id === "Equipment" &&
          ["ALL","Inventory",o.true_id].includes(view)).sort((a,b) => {return a.name.localeCompare(b.name)}).map((cont, key) => {
          return (
            <Grid
              key={key}
              size={{
                xs: 12
              }}>
              { renderContainer(cont, filtered) }
            </Grid>
          );
        })}
        { inventory.containers.filter(o => o.true_id === "Campaign Storage" &&
          ["ALL","Inventory",o.true_id].includes(view)).sort((a,b) => {return a.name.localeCompare(b.name)}).map((cont, key) => {
          return (
            <Grid
              key={key}
              size={{
                xs: 12
              }}>
              { renderContainer(cont, filtered) }
            </Grid>
          );
        })}
        { inventory.containers.filter(o => !["Equipment","Storage","Campaign Storage"].includes(o.true_id) &&
          ["ALL","Inventory",o.true_id].includes(view)).sort((a,b) => {return a.name.localeCompare(b.name)}).map((cont, key) => {
          return (
            <Grid
              key={key}
              size={{
                xs: 12
              }}>
              { renderContainer(cont, filtered) }
            </Grid>
          );
        })}
        { inventory.containers.filter(o => o.true_id === "Storage" &&
          ["ALL","Inventory",o.true_id].includes(view)).sort((a,b) => {return a.name.localeCompare(b.name)}).map((cont, key) => {
          return (
            <Grid
              key={key}
              size={{
                xs: 12
              }}>
              { renderContainer(cont, filtered) }
            </Grid>
          );
        })}
      </>
    );
  }

  const renderContainer = (cont: CharacterContainer, 
    filtered: CharacterItem[]) => {
    // Breaking this up into containers and equipment
    const option_map: IStringHash = {};
    inventory.containers.filter(o => cont.true_id !== o.true_id).forEach(cont2 => {
      option_map[cont2.name] = cont2.true_id;
    });
    // console.log(cont);
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <CharacterContainerItem
            obj={char}
            inventory={inventory}
            cont={cont}
            disabled={disabled}
            filtered={filtered}
            // log_it={!!cont.base_item}
            reload_time={reload_time}
            onOpen={(open_item) => {
              setDetailsItem(open_item);
              setDrawer("details");
            }}
          />
        </Grid>
      </Grid>
    );
  }

  const renderSpecial = () => {
    // debugger;
    if (char instanceof Character) {
      const filtered = char.actions.filter(o => 
        o.action instanceof CharacterAbility &&
        o.action.the_ability instanceof Ability &&
        o.action.the_ability.show_in.includes("Inventory"));
      return (
        <Grid size={12}
          container
          spacing={0}
          direction="row">
          <Grid size={12}
            style={{
              fontSize: "14px",
            }}
            className={`header`}>
            &nbsp;Special
          </Grid>
          { filtered.map((action) => {
            // console.log(action);
            if (reloading) {
              return (<span key={action.true_id}></span>);
            } else {
              return (
                <Grid
                  key={action.true_id}
                  size={{
                    xs: 12
                  }}>
                  <CharacterActionInput
                    char_int={char}
                    action={action}
                    processing={false}
                    shared={false}
                    level={action.level}
                    onOpen={() => {
                      setDetailsAction(action);
                      setDrawer("details");
                    }}
                  />
                </Grid>
              );
            }
          })}
        </Grid>
      );
    }
    return null;
  }

  const renderOtherPossessions = () => {
    const enabled = !disabled && api.user !== null;
    return (
      <Grid
        container
        spacing={0}
        direction="row"
        size={{
          xs: 12
        }}>
        <Grid
          style={{
            fontSize: "12px",
            fontWeight: "bold",
          }}
          size={{
            xs: 12
          }}>
          &nbsp;Other Possessions
        </Grid>
        { edit_view ? 
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name=""
              multiline
              disabled={ !enabled }
              value={ inventory.other_possessions }
              onBlur={(changed: string) => {
                setEditView(false);
                inventory.other_possessions = changed;
                char.subscription_holder.itemsChanged();
              }}
            />
          </Grid>
        :
          <Grid
            onClick={() => {
              setEditView(true);
            }}
            size={{
              xs: 12
            }}>
            { inventory.other_possessions }
          </Grid>
        }
      </Grid>
    );
  }

  const enabled = !disabled && api.user !== null && (char.owner_id === api.user._id || (char.owner_id === api.user_id || (char instanceof Character && char.campaign && char.campaign.owner_id === api.user._id)));
  
  const renderExtras = () => {
    const action = details_action;
    const open = drawer === "details";
    if (action && open) {
      // console.log(action);
      let char_item: CharacterItem | undefined = undefined;
      if (action.action instanceof CharacterItem) {
        char_item = action.action;
      } else {
        if (action.action instanceof CharacterAbility && action.action.item_id !== "") {
          const item_id = action.action.item_id;
          char_item = inventory.items.find(o => o.true_id === item_id);
        }
      }
      return (
        <>
          <ModalOrDrawer
            open={ drawer === "details" } 
            onClose={() => {
              setDrawer("");
              setDetailsAction(null);
              char.subscription_holder.modalClosed();
            }}>
            { char_item ? 
              <CharacterItemDetailsSidebar
                char={char}
                char_item={char_item}
                shared={shared}
                onRemove={() => {
                  setDrawer("");
                  setDetailsAction(null);
                }}
              /> 
            : (char instanceof Character &&
                (action.action instanceof CharacterSpell || 
                  action.action instanceof CharacterAbility)) ? 
              <CharacterSpellDetails
                char_int={char}
                obj={ action.action }
                level={ action.level }
                type={action.type}
                action={action}
                shared={false}
                show_item
                onObjChange={(obj: CharacterSpell | CharacterAbility) => {
                }}
                onClose={() => {
                  setDrawer("");
                  setDetailsAction(null);
                  // setLevel(null);
                }}
              /> 
            : (char instanceof Character && action.action instanceof Attack) && 
              <CharacterAttackSidebar
                char={char}
                attack={action.action}
              />
            }
          </ModalOrDrawer>
        </>
      );
    } else {
      return (
        <>
          <ModalOrDrawer
            open={ drawer === "currency" } 
            onClose={() => {
              setDrawer("");
              setDetailsAction(null);
              char.subscription_holder.modalClosed();
            }}>
            <CharacterCurrency
              char={char}
              inventory={inventory}
            />
          </ModalOrDrawer>
          <ModalOrDrawer
            open={ drawer === "details" } 
            // drawer_only
            onClose={() => {
              setDrawer("");
              setDetailsAction(null);
              char.subscription_holder.modalClosed();
            }}>
            { drawer === "details" && details_item &&
              <CharacterItemDetailsSidebar
                char={char}
                char_item={details_item}
                shared={shared}
                onRemove={() => {
                  setDrawer("");
                  setDetailsAction(null);
                }}
              />
            }
          </ModalOrDrawer>
        </>
      );
    }
  }
  
  let menu_option_map: IStringHash = {
    "ALL": "ALL",
    "Inventory": "Inventory",
  };
  inventory.containers.forEach(c => {
    menu_option_map[c.name] = c.true_id;
  });
  menu_option_map = char instanceof Campaign ? {
    ...menu_option_map,
  } : {
    ...menu_option_map,
    "Attunement": "Attunement",
    "Other Possessions": "Other Possessions",
    "Special": "Special"
  };
  console.log("Rendering Char View Inv");
  
  console.log(inventory);
  return (
    <Grid container spacing={0} direction="row" className="curved_border bg_black_transp" 
      style={{
        fontSize: "11px"
      }}>
      <Grid size={6}>
        { char instanceof Character &&
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "left"
              }}>
              <Grid container spacing={0} direction="row" 
                style={{
                  color: (Math.round(char.weight_carried*100)/100 > char.max_carrying_capacity ? "red" : "inherit")
                }}>
                <Grid size={12}
                  style={{
                    fontSize: "13px",
                    fontWeight: "bold",
                  }}>
                  Weight Carried: { Math.round(char.weight_carried*100)/100 } lb. 
                </Grid>
                <Grid size={12} style={{ fontWeight: "bold" }}>
                  { char.encumbrance_level === 3 ?
                    <span style={{ color: "red" }}>Over Carrying Capacity</span>
                  : char.encumbrance_level === 2 ?
                    <span style={{ color: "orange" }}>Heavily Encumbered</span>
                  : char.encumbrance_level === 1 ?
                    <span style={{ color: "#f3b65a" }}>Lightly Encumbered</span>
                  : Math.round(char.weight_carried*100)/100 > char.max_carrying_capacity ? 
                    <span style={{ color: "red" }}>Over Carrying Capacity</span>
                  :
                    "Unencumbered"
                  }
                </Grid>
              </Grid>
            </div>
          </>
        }
      </Grid>
      <Grid
        size={{
          xs: 6
        }}>
        { renderCurrency() }
      </Grid>
      { !disabled && (char instanceof Campaign || inventory instanceof CharacterInventory) &&
        <>
          <Grid size={2}>
          </Grid>
          <Grid size={8}>
            <CharacterManageInventoryButton
              char={char}
              inventory={inventory}
              disabled={!enabled}
              shared={shared}
            />
          </Grid>
          <Grid size={2}>
          </Grid>
        </>
      }
      { char instanceof Character && inventory instanceof CharacterInventory && (inventory.items.filter(o => !["Equipment","Storage","Campaign Storage"].includes(o.true_id)).length === 0) ?
        <Grid size={12}>
          <CharacterStartingEquipment
            char={char}
          />
        </Grid>
      :
        <>
          <Grid size={12}>
            <div style={{ margin: "4px" }}>
              <StringBox
                name="Search Items"
                value={ search_string }
                onChange={setSearchString}
              />
            </div>
          </Grid>
          <Grid size={12}>
            <FloatMenu
              option_map={menu_option_map}
              selected={view}
              onSelect={setView}
            />
          </Grid>
          { (!["Attunement","Other"].includes(view)) &&
            renderInventory()
          }
          { char instanceof Character && inventory instanceof CharacterInventory && (view === "ALL" || view === "Attunement") &&
            <CharacterAttunement 
              character={char} 
              disabled={disabled}           
            />
          }
          { char instanceof Character && inventory instanceof CharacterInventory && (view === "ALL" || view === "Special") && renderSpecial() }
          { char instanceof Character && inventory instanceof CharacterInventory && (view === "ALL" || view === "Other") &&
            renderOtherPossessions()
          }
        </>
      }
      { renderExtras() }
    </Grid>
  );
}
